<template>
  <v-card class="overflow-hidden live-tracking-sidebar px-2">
    <v-menu offset-y>
      <template v-slot:activator="{ on, attrs, value }">
        <span class="black--text d-flex align-center w-100 border-bottom live-tracking-sidebar-menu" v-bind="attrs"
          v-on="on">
          <span class="font-14 font-weight-bold">{{
            trackingVisitStatus?.text
          }}</span>
          <v-avatar class="mx-2" size="23" :color="trackingVisitStatus?.color">
            <span class="white--text font-weight-medium">{{
              trackingVisitStatus?.count
            }}</span>
          </v-avatar>
          <v-icon v-if="value">mdi-chevron-up</v-icon>
          <v-icon v-else>mdi-chevron-down</v-icon>
        </span>
      </template>
      <v-list tile>
        <template v-for="(row, index) in trackingVisitStatusList">
          <v-list-item :key="`visit-status-list-${index}`" @click="updateVisitStatus(row)" link>
            <v-list-item-title class="font-14">
              {{ row.text }}
            </v-list-item-title>
            <v-list-item-avatar class="my-0">
              <v-avatar size="23" :color="row.color">
                <span class="white--text font-weight-medium">{{
                  row.count
                }}</span>
              </v-avatar>
            </v-list-item-avatar>
          </v-list-item>
          <v-divider v-if="row?.engineer" :key="`visit-status-divider-${index}`" />
        </template>
      </v-list>
    </v-menu>
    <v-sheet height="calc(100vh - 111px)" class="overflow-y-auto">
      <v-text-field dense filled solo flat color="cyan" hide-details v-model="search" clearable
        placeholder="Search jobs..." append-icon="mdi-magnify" @input="filterVisit" class="my-3"
        :loading="trackingVisitLoading" />
      <div style="max-height: calc(100vh - 172px); overflow-y: scroll">
        <div v-if="trackingVisitLoading" class="d-flex justify-center align-center my-4">
          <v-progress-circular indeterminate color="cyan" />
        </div>
        <div v-else>
          <template v-if="trackingVisits.length">
            <v-card v-for="(row, index) in trackingVisits" :key="`live-tracking-visits-${index}`" class="ma-0 rounded-0"
              @click="$emit('hoverVisitMarker', row?.uuid)" @dblclick="openDialog(row)" elevation="0" :class="[
                { 'active-card': row?.uuid === activeVisitUuid },
                { 'border-top': index === 0 },
                { 'border-bottom': trackingVisits[index + 1] },
              ]">
              <v-card-text>
                <div class="d-flex align-center">
                  <span class="fw-700 blue--text text-darken-4" style="font-size: 16px">
                    #{{ row.barcode }}
                  </span>
                  <v-spacer></v-spacer>
                  <div>
                    <div class="green--text">
                      <v-icon color="green" x-small>mdi-alarm</v-icon>
                      {{ formatDateTime(row.started_at) }}
                    </div>
                    <div class="red--text">
                      <v-icon color="red" x-small>mdi-alarm</v-icon>
                      {{ formatDateTime(row.finished_at) }}
                    </div>
                  </div>
                </div>
                <div class="d-flex align-center">
                  <div class="text-truncate d-block fw-500">
                    {{ row.title }}
                  </div>
                  <v-spacer></v-spacer>
                  <v-icon small :color="row.status_color">mdi-circle</v-icon>
                </div>
              </v-card-text>
            </v-card>
          </template>
          <template v-else>
            <v-card class="ma-0 rounded-0" elevation="0">
              <v-card-text class="text-center">
                <span class="font-weight-bold font-14">
                  No jobs found for date - {{ formatDate(date) }}
                </span>
              </v-card-text>
            </v-card>
          </template>
        </div>
      </div>
    </v-sheet>
    <Dialog :dialog="viewDialog" :dialog-width="dialogWidth">
      <template v-slot:title>
        <div class="d-flex align-center justify-space-between w-100">
          <div class="d-flex align-center">
            <template v-if="viewLoading">
              <v-skeleton-loader type="chip" />
            </template>
            <template v-else>
              <div>Visit #{{ viewVisit?.visit?.barcode }}</div>
              <div>
                <v-chip
                  class="ml-2 my-0 py-0 custom-status font-weight-600 custom-grey-border text-uppercase justify-center font-10"
                  :color="getStatusColor(viewVisit?.visit?.status)" label style="max-height: 25px" text-color="white">
                  {{ viewVisit?.visit?.status_text }}
                </v-chip>
              </div>
              <div v-if="viewVisit?.visit?.enroute">
                <v-chip label class="justify-center font-10 mt-2" text-color="white" color="#2a806b">
                  Enroute
                </v-chip>
              </div>
              <div v-if="viewVisit?.visit?.reschedule_count > 0">
                <v-tooltip bottom content-class="custom-top-tooltip">
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip v-bind="attrs" v-on="on"
                      class="mr-2 my-1 custom-status font-weight-600 custom-grey-border d-inline-table text-uppercase justify-center font-10 text-white"
                      color="red lighten-1" label style="max-height: 25px" outlined>
                      Revision ({{ viewVisit?.visit?.reschedule_count }})
                    </v-chip>
                  </template>
                  <span>Revision ({{ viewVisit?.visit?.reschedule_count }})</span>
                </v-tooltip>
              </div>
              <div v-if="viewVisit?.visit?.followup">
                <v-tooltip bottom content-class="custom-top-tooltip">
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip v-bind="attrs" v-on="on" class="mr-2 my-1 d-inline-table font-weight-600" :color="`${getFollowColor(
                      viewVisit?.visit?.followup?.status
                    )} white--text`" label style="max-height: 25px" outlined>
                      <template v-if="viewVisit?.visit?.followup?.status == 2">Follow Up Closed</template>
                      <template v-else> Follow Up Open</template>
                    </v-chip>
                  </template>
                  <span>
                    <template v-if="viewVisit?.visit?.followup?.status == 2">Follow Up Closed</template>
                    <template v-else> Follow Up Open</template></span>
                </v-tooltip>
              </div>
              <div v-if="
                viewVisit?.visit?.closer?.need_cost == 1 &&
                viewVisit?.visit?.service_form_count > 0
              ">
                <v-icon :color="'red'" style="height: 25px; width: 25px; padding: 0" class="mr-2 my-1">
                  mdi-flag</v-icon>
              </div>
            </template>
          </div>
          <div>
            <template v-if="viewLoading">
              <div class="d-flex">
                <v-skeleton-loader type="chip" />
                <v-skeleton-loader class="ml-4" type="chip" />
              </div>
            </template>
            <template v-else>
              <v-rating v-if="
                viewVisit?.visit?.status == 4 &&
                viewVisit?.visit_rating?.rating_type == 'star'
              " readonly dense :value="viewVisit?.visit_rating?.rating" background-color="orange lighten-3"
                color="orange" />
              <v-rating color="" :value="viewVisit?.visit_rating?.rating" readonly v-if="
                viewVisit?.visit?.status == 4 &&
                viewVisit?.visit_rating?.rating_type == 'number'
              ">
                <template v-slot:item="props">
                  <v-avatar class="mx-1" style="border: 2px solid orange"
                    :color="props.isFilled ? 'orange text-white' : ''" size="30">
                    <span class="text-h6">{{ props.index + 1 }}</span>
                  </v-avatar>
                </template>
              </v-rating>
              <template v-if="engineerList.length">
                <v-menu v-model="engineerMenu" bottom left offset-y :close-on-content-click="false">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn class="mx-2 custom-bold-button white--text" color="cyan" :disabled="apptEngLoading"
                      :loading="apptEngLoading" v-bind="attrs" v-on="on">
                      <v-icon class="mr-2">mdi-account-group</v-icon>Assign
                    </v-btn>
                  </template>
                  <v-list class="light-gray-background pb-0" min-width="340">
                    <v-list-item-title class="text-uppercase font-weight-700 color-custom-blue font-size-16 p-2 pt-0">
                      Select Team
                    </v-list-item-title>
                    <perfect-scrollbar :options="{ suppressScrollX: true }" class="scroll custom-box-top-inner-shadow"
                      style="max-height: 50vh; position: relative">
                      <v-list-item class="white-background" v-for="(team, index) in engineerList" :key="index">
                        <v-checkbox v-model="newEngineers" :label="team.display_name" :value="team.id" hide-details
                          color="cyan" class="mr-4 mt-0" :disabled="apptEngLoading" multiple>
                          <template v-slot:label>
                            <v-list-item>
                              <v-list-item-avatar>
                                <v-img :lazy-src="$defaultProfileImage" :src="$assetAPIURL(team.profile_logo)"></v-img>
                              </v-list-item-avatar>
                              <v-list-item-content>
                                <v-tooltip top content-class="custom-top-tooltip">
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-list-item-title v-bind="attrs" v-on="on" class="font-size-16 font-weight-500"
                                      v-html="team.display_name"></v-list-item-title>
                                  </template>
                                  <span class="text-capitalize">
                                    <template v-if="team.skills">{{
                                      team.skills.join(", ")
                                    }}</template>
                                    <template v-else-if="team.designation">{{
                                      team.designation.value
                                    }}</template>
                                    <template v-else><em>No Skills</em></template>
                                  </span>
                                </v-tooltip>
                                <v-list-item-subtitle v-if="
                                  lodash.isEmpty(team.designation) == false
                                ">{{
                                  team.designation.value
                                }}</v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>
                          </template>
                        </v-checkbox>
                      </v-list-item>
                    </perfect-scrollbar>
                    <v-btn :disabled="apptEngLoading" :loading="apptEngLoading" v-on:click="updateEngineer()"
                      color="cyan white--text" class="w-100 text-uppercase font-weight-700 font-size-16">Save</v-btn>
                  </v-list>
                </v-menu>
              </template>
              <template v-else>
                <v-btn v-if="
                  viewVisit?.visit?.status != 4 &&
                  viewVisit?.visit?.status != 5
                " depressed :disabled="apptEngLoading || viewLoading" :loading="apptEngLoading"
                  class="mx-2 custom-bold-button white--text" color="cyan" @click="getAvailableEngineers">
                  Get Available Teams
                </v-btn>
              </template>
              <v-btn depressed @click="viewDialog = false" class="custom-bold-button">Close</v-btn>
            </template>
          </div>
        </div>
      </template>
      <template v-slot:body>
        <div v-if="viewLoading" class="d-flex align-center justify-center">
          <div>
            <v-progress-circular :size="70" :width="7" indeterminate color="cyan" />
            <br />
            <p class="mt-4 font-size-18 font-weight-500">Please Wait...</p>
          </div>
        </div>
        <v-row v-else>
          <v-col cols="4" sm="3">
            <div>
              <table width="100%">
                <tr>
                  <td valign="top" style="width: 95px" class="py-1 font-size-18 font-weight-500">
                    Visit #
                  </td>
                  <td valign="middle" class="py-1 font-size-18">
                    <label class="m-0" v-if="viewVisit?.visit?.barcode">
                      <Barcode :barcode="viewVisit?.visit?.barcode"></Barcode>
                    </label>
                  </td>
                </tr>
                <tr>
                  <td valign="top" class="py-1 font-size-18 font-weight-500">
                    Customer
                  </td>
                  <td valign="middle" class="py-1 font-size-18">
                    <router-link v-if="viewVisit?.ticket?.customer?.id" :to="getDefaultRoute('customer.detail', {
                      params: {
                        id: viewVisit?.ticket?.customer?.id,
                      },
                    })
                      ">
                      <v-chip small color="blue darken-4" class="cursor-pointer" outlined label>
                        {{ viewVisit?.ticket?.customer?.display_name }}
                      </v-chip>
                    </router-link>
                  </td>
                </tr>
                <tr>
                  <td valign="top" class="py-1 font-size-18 font-weight-500">
                    Reference #
                  </td>
                  <td valign="middle" class="py-1 font-size-18">
                    <label class="m-0" v-if="viewVisit?.ticket?.reference">{{
                      viewVisit?.ticket?.reference
                    }}</label>
                    <em v-else class="text-muted"> no refernce </em>
                  </td>
                </tr>
                <tr>
                  <td valign="top" class="py-1 font-size-18 font-weight-500">
                    Appointment
                  </td>
                  <td valign="middle" class="py-1 font-size-18">
                    <span style="color: #24326d !important" v-html="getAppointmentText">
                    </span>
                  </td>
                </tr>
                <tr>
                  <td valign="middle" class="py-1 font-size-18 font-weight-500">
                    Priority
                  </td>
                  <td valign="middle" class="py-1 font-size-18">
                    <Priority v-if="viewVisit?.ticket?.priority" class="my-1 ml-n2"
                      :priority="viewVisit?.ticket?.priority" small></Priority>
                  </td>
                </tr>
                <tr>
                  <td valign="top" class="py-1 font-size-18 font-weight-500">
                    Type
                  </td>
                  <td valign="middle" class="py-1 font-size-18">
                    <v-tooltip top content-class="custom-top-tooltip">
                      <template v-slot:activator="{ on, attrs }">
                        <v-chip v-bind="attrs" v-on="on" class="mr-2 my-1 font-weight-600" color="chip-custom-blue"
                          label small outlined>
                          {{ getScheduleType }}
                        </v-chip>
                      </template>
                      <span>Schedule Type</span>
                    </v-tooltip>
                  </td>
                </tr>
                <tr v-if="viewVisit?.visit?.actual_start_date">
                  <td valign="top" class="py-1 font-size-18 font-weight-500">
                    Actual Job Start Date
                  </td>
                  <td valign="middle" class="py-1 font-size-18">
                    <label class="m-0">
                      <v-chip small outlined color="red" label>
                        {{ formatDate(viewVisit?.visit?.actual_start_date) }}
                        {{
                          formatDateStartTime(
                            viewVisit?.visit?.actual_start_date
                          )
                        }}
                      </v-chip>
                    </label>
                  </td>
                </tr>
                <tr v-if="viewVisit?.visit?.actual_end_date">
                  <td valign="top" class="py-1 font-size-18 font-weight-500">
                    Actual Job End Date
                  </td>
                  <td valign="middle" class="py-1 font-size-18">
                    <label class="m-0">
                      <v-chip small outlined color="green" label>
                        {{ formatDate(viewVisit?.visit?.actual_end_date) }}
                        {{
                          formatDateStartTime(viewVisit?.visit?.actual_end_date)
                        }}
                      </v-chip>
                    </label>
                  </td>
                </tr>
                <tr v-if="viewVisit?.ticket?.schedule_message">
                  <td valign="top" class="py-1 font-size-18 font-weight-500">
                    Schedule
                  </td>
                  <td valign="middle" class="py-1 font-size-18">
                    <label class="m-0">{{
                      viewVisit?.ticket?.schedule_message
                    }}</label>
                  </td>
                </tr>
                <tr v-if="viewVisit?.visit?.cancel_reason">
                  <td valign="top" class="py-1 font-size-18 font-weight-600 d-flex">
                    Cancel Reason
                  </td>
                  <td valign="middle" class="py-1 font-size-18">
                    <label class="m-0">{{
                      viewVisit?.visit?.cancel_reason
                    }}</label>
                  </td>
                </tr>
                <tr>
                  <td valign="top" class="py-1 font-size-18 font-weight-500">
                    Tag
                  </td>
                  <td valign="middle" class="py-1 font-size-18">
                    <template v-if="viewVisit?.ticket?.tags.length">
                      <v-chip v-for="(row, index) in viewVisit?.ticket?.tags" :key="index" class="mr-1 mb-1"
                        text-color="white" small label :color="row.color">
                        <span class="">
                          {{ row.text }}
                        </span>
                      </v-chip>
                    </template>
                    <em v-else class="text-muted"> no tags</em>
                  </td>
                </tr>
                <tr v-if="viewVisit?.visit?.followup?.follow_up_date">
                  <td valign="top" class="py-1 font-size-18 font-weight-500">
                    Follow Up Date
                  </td>
                  <td valign="middle" class="py-1 font-size-18">
                    <label class="m-0" v-if="viewVisit?.visit?.followup?.follow_up_date">
                      <v-chip small outlined color="blue" label>
                        {{
                          formatDate(viewVisit?.visit?.followup.follow_up_date)
                        }}
                      </v-chip></label>
                    <em v-else class="text-muted"> no follow up date</em>
                  </td>
                </tr>
              </table>
            </div>
            <div class="mt-5" v-if="
              viewVisit?.visit?.contract > 0 &&
              viewVisit?.visit?.contract_detail
            ">
              <v-flex md12>
                <table width="100%">
                  <tr>
                    <th colspan="3" class="grey lighten-4 font-size-19 color-custom-blue py-1">
                      Contract Information
                    </th>
                  </tr>
                  <tr>
                    <td valign="middle" width="60px" class="py-1 font-size-18 font-weight-500">
                      Title
                    </td>
                    <td align="center" class="py-1" width="20px">:</td>
                    <td valign="middle" class="py-1 font-size-18">
                      <label class="m-0">
                        {{
                          viewVisit?.visit?.contract_viewVisit?.ticket
                            ?.job_title
                        }}
                      </label>
                    </td>
                  </tr>
                  <tr>
                    <td valign="middle" class="py-1 font-size-18 font-weight-500">
                      Start
                    </td>
                    <td align="center" class="py-1">:</td>
                    <td valign="middle" class="py-1 font-size-18">
                      <label class="m-0">
                        {{
                          formatDate(
                            viewVisit?.visit?.contract_viewVisit?.ticket
                              ?.contract_start
                          )
                        }}</label>
                    </td>
                  </tr>
                  <tr>
                    <td valign="middle" class="py-1 font-size-18 font-weight-500">
                      End
                    </td>
                    <td align="center" class="py-1">:</td>
                    <td valign="middle" class="py-1 font-size-18">
                      <label class="m-0">
                        {{
                          formatDate(
                            viewVisit?.visit?.contract_viewVisit?.ticket
                              ?.contract_end
                          )
                        }}</label>
                    </td>
                  </tr>
                </table>
              </v-flex>
            </div>
            <div class="mt-5">
              <v-flex md12>
                <table width="100%">
                  <tr>
                    <th class="grey lighten-4 font-size-16 text-uppercase color-custom-blue py-1">
                      Site Location
                    </th>
                  </tr>
                  <tr v-if="viewVisit?.ticket?.property?.property_address">
                    <td class="py-0 font-size-18 py-2">
                      <template>
                        <div class="custom-flex-d" v-if="viewVisit?.ticket?.property?.property_name">
                          <b>
                            <v-icon style="font-size: 18px; margin-top: -3px" class="cmt-2">
                              mdi-home-map-marker
                            </v-icon>
                          </b>
                          <span>{{ viewVisit?.ticket?.property?.property_name
                            }}<br /></span>
                        </div>
                        <div v-else>
                          <em class="text-muted">
                            <v-icon style="font-size: 18px; margin-top: -3px" class="cmt-2">
                              mdi-home-map-marker
                            </v-icon>
                            no Site Name</em>
                        </div>
                      </template>
                      <template>
                        <div class="custom-flex-d">
                          <b>
                            <v-icon style="font-size: 18px; margin-top: -3px" class="cmt-2">
                              mdi-map-marker
                            </v-icon>
                          </b>
                          <span>
                            <template v-if="viewVisit?.ticket?.property?.unit_no">{{
                              viewVisit?.ticket?.property?.unit_no
                              }},</template>
                            <template v-if="viewVisit?.ticket?.property?.street_1">{{
                              viewVisit?.ticket?.property?.street_1
                              }},</template>
                            {{ viewVisit?.ticket?.property?.street_2 }}
                            <br />
                            {{ viewVisit?.ticket?.property?.country }}
                            <template v-if="
                              viewVisit?.ticket?.property?.zip_code &&
                              viewVisit?.ticket?.property?.zip_code !=
                              '000000'
                            ">
                              {{ viewVisit?.ticket?.property?.zip_code }}
                            </template>
                          </span>
                        </div>
                      </template>
                    </td>
                  </tr>
                  <tr v-if="viewVisit?.visit?.location?.id">
                    <th class="grey lighten-4 font-size-16 color-custom-blue py-1">
                      Other Location
                    </th>
                  </tr>
                  <td class="py-0 font-size-18 py-2" v-if="viewVisit?.visit?.location?.id">
                    <template v-if="viewVisit?.visit?.location?.id">
                      <span>
                        <!--   <v-icon small>mdi-map-marker</v-icon>--></span>
                      <p class="mr-2" v-if="viewVisit?.visit?.location">
                        {{ viewVisit?.visit?.location?.street_1 }},
                        <br v-if="
                          viewVisit?.visit?.location?.street_2 ||
                          viewVisit?.visit?.location?.unit_no
                        " />
                        <template v-if="viewVisit?.visit?.location?.street_2">
                          {{ viewVisit?.visit?.location?.street_2 }},
                        </template>
                        <template v-if="viewVisit?.visit?.location?.unit_no">
                          {{ viewVisit?.visit?.location?.unit_no }},
                        </template>
                        <br />
                        {{ viewVisit?.visit?.location?.country }}
                        <template v-if="
                          viewVisit?.visit?.location &&
                          viewVisit?.visit?.location?.zip_code != '000000'
                        ">
                          , {{ viewVisit?.visit?.location?.zip_code }}
                        </template>
                      </p>
                    </template>
                  </td>

                  <tr>
                    <th class="grey lighten-4 font-size-16 text-uppercase color-custom-blue py-1">
                      Contact details
                    </th>
                  </tr>
                  <tr v-if="viewVisit?.ticket?.property_person">
                    <td class="py-0 font-size-18">
                      <template v-if="viewVisit?.ticket?.property_person.display_name">
                        {{ viewVisit?.ticket?.property_person.display_name }}
                      </template>
                    </td>
                  </tr>
                  <tr v-if="viewVisit?.ticket?.property_person">
                    <td class="py-0 font-size-18">
                      <template v-if="viewVisit?.ticket?.property_person.primary_phone">
                        {{ viewVisit?.ticket?.property_person.primary_phone }}
                      </template>
                    </td>
                  </tr>
                  <tr v-if="viewVisit?.ticket?.property_person">
                    <td class="py-0 font-size-18">
                      <template v-if="viewVisit?.ticket?.property_person.primary_email">
                        {{ viewVisit?.ticket?.property_person.primary_email }}
                        <v-icon v-if="viewVisit?.ticket?.property_person_notify" v-tippy="{
                          arrow: true,
                          arrowType: 'round',
                          animation: 'fade',
                        }" content="Notification was sent to given email" color="green lighten-1">mdi-check</v-icon>
                      </template>
                    </td>
                  </tr>
                </table>
                <table width="100%">
                  <template v-if="
                    viewVisit?.visit?.invoice_needed &&
                    viewVisit?.is_visit_invoice_allow
                  ">
                    <tr>
                      <th colspan="2" class="grey lighten-4 font-size-16 text-uppercase color-custom-blue py-1">
                        Invoicing
                      </th>
                    </tr>
                    <tr v-if="viewVisit?.visit?.invoice_needed">
                      <td valign="middle" class="py-1 font-size-18 font-weight-500">
                        Is Invoice Needed
                      </td>
                      <td valign="middle" class="py-1 font-size-18">
                        <label class="m-0" v-if="viewVisit?.visit?.invoice_needed">
                          <v-chip color="chip-custom-blue" outlined text-color="" small label>
                            YES</v-chip>
                        </label>
                        <em class="text-muted" v-else>
                          no internal reference</em>
                      </td>
                    </tr>
                    <tr v-if="viewVisit?.visit?.payment_due">
                      <td valign="middle" class="py-1 font-size-18 font-weight-500">
                        Payment Due
                      </td>
                      <td valign="middle" class="py-1 font-size-18">
                        <label class="m-0 font-weight-700" v-if="viewVisit?.visit?.payment_due">
                          {{ getDueDay(viewVisit?.visit?.payment_due) }} NET
                        </label>
                      </td>
                    </tr>
                    <tr v-if="viewVisit?.visit?.payment_due">
                      <td valign="middle" class="py-1 font-size-18 font-weight-500">
                        Send To Customer
                      </td>
                      <td valign="middle" class="py-1 font-size-18">
                        <label class="m-0" v-if="viewVisit?.visit?.send_invoice">
                          <v-chip color="chip-custom-blue" outlined text-color="" small label>
                            YES</v-chip>
                        </label>
                        <label class="m-0" v-else>
                          <v-chip color="chip-custom-blue" outlined text-color="" small label>
                            No</v-chip>
                        </label>
                      </td>
                    </tr>
                    <tr v-if="viewVisit?.visit?.invoice_main_type">
                      <td valign="middle" class="py-1 font-size-18 font-weight-500">
                        Invoice Criteria
                      </td>
                      <td valign="middle" class="py-1 font-size-18">
                        <label class="m-0 text-capitalize font-weight-700" v-if="viewVisit?.visit?.invoice_main_type">
                          {{
                            viewVisit?.visit?.invoice_main_type.replaceAll(
                              "_",
                              " "
                            )
                          }}
                        </label>
                      </td>
                    </tr>
                    <tr v-if="viewVisit?.visit?.invoice_type">
                      <td valign="middle" class="py-1 font-size-18 font-weight-500">
                        Invoice Type
                      </td>
                      <td valign="middle" class="py-1 font-size-18">
                        <label class="m-0 text-capitalize font-weight-700" v-if="viewVisit?.visit?.invoice_type">
                          {{
                            viewVisit?.visit?.invoice_type.replaceAll("_", " ")
                          }}
                        </label>
                      </td>
                    </tr>
                  </template>
                </table>
              </v-flex>
            </div>
          </v-col>
          <v-col cols="8" sm="9" class="border-left">
            <h1 class="color-custom-blue custom-nowrap-ellipsis text-capitalize mr-2 font-weight-700"
              v-if="viewVisit?.ticket?.title">
              Trip:
              <template v-if="viewVisit?.visit?.trip_count > 0">{{ viewVisit?.visit?.barcode }}-{{
                viewVisit?.visit?.trip_count
                }}</template>
              <template v-else>{{ viewVisit?.visit?.barcode }}-01</template> /
              {{ viewVisit?.ticket?.title }}
            </h1>
            <v-tabs active-class="custom-tab-active" v-model="ticketTab" background-color="transparent" color="cyan"
              class="custom-tab-transparent tab-sticky" show-arrows>
              <v-tab class="font-size-16 font-weight-600 px-8" href="#overview">
                <span class="svg-icon svg-icon-lg mr-4 svg-v-list-icon">
                  <!--begin::Svg Icon-->
                  <inline-svg :src="$assetURL('media/custom-svg/overview.svg')" />
                  <!--end::Svg Icon-->
                </span>
                Overview
              </v-tab>
              <v-tab class="font-size-16 font-weight-600 px-8" href="#team">
                <span class="svg-icon svg-icon-lg mr-4 svg-v-list-icon">
                  <!--begin::Svg Icon-->
                  <inline-svg :src="$assetURL('media/custom-svg/engineer.svg')" />
                  <!--end::Svg Icon-->
                </span>
                Team
              </v-tab>
              <v-tabs-items v-model="ticketTab">
                <v-tab-item value="overview">
                  <v-container fluid class="px-0">
                    <v-card v-if="viewVisit?.visit?.followup?.remark" flat
                      class="custom-grey-border remove-border-radius mb-4">
                      <v-card-title class="headline grey lighten-4">
                        <span class="font-weight-700 custom-headline color-custom-blue font-size-16">Follow Up Remark
                        </span>
                      </v-card-title>
                      <v-card-text class="p-6 font-size-16">
                        <read-more class="mx-4 custom-read-more font-weight-600 font-size-18 pb-1 custom-gray-color"
                          more-str="read more" :text="viewVisit.visit.followup.remark ||
                            '<em>No Description</em>'
                            " link="#" less-str="read less" :max-chars="200">
                        </read-more>
                      </v-card-text>
                    </v-card>
                    <v-row>
                      <v-col md="6">
                        <v-card flat class="custom-grey-border remove-border-radius mb-4 h-100">
                          <v-card-title class="headline grey lighten-4 py-1">
                            <span class="font-weight-700 custom-headline color-custom-blue font-size-16 text-uppercase">
                              Information</span>
                          </v-card-title>
                          <v-card-text class="p-6 font-size-16">
                            <v-flex md12>
                              <table width="100%">
                                <tr v-if="
                                  viewVisit?.visit?.prevntive_visit?.length >
                                  0 && showModules.preventive_maintanance
                                ">
                                  <td width="170px" valign="top" class="py-1 font-size-18 font-weight-500">
                                    Consumed PM
                                  </td>
                                  <td align="center">:</td>
                                  <td width="170px" valign="middle" class="py-1 font-size-18">
                                    <v-tooltip v-for="(row, index) in viewVisit.visit
                                      .prevntive_visit" :key="'ticket-type' + index" top
                                      content-class="custom-top-tooltip">
                                      <template v-slot:activator="{ on, attrs }">
                                        <v-chip v-bind="attrs" v-on="on" class="mr-2 font-weight-600" color="red" small
                                          outlined v-on:click.prevent.stop="
                                            routeToDetailLink(
                                              row?.preventive?.id,
                                              'admin.preventive-maintanance.detail'
                                            )
                                            ">
                                          {{ row?.preventive?.barcode }}
                                        </v-chip>
                                      </template>
                                      <span>Consumed PM</span>
                                    </v-tooltip>
                                  </td>
                                </tr>
                                <tr v-if="viewVisit?.visit_flag?.value == 1">
                                  <td valign="middle" width="170px" class="py-1 font-size-18 font-weight-500">
                                    Is Service Form
                                  </td>
                                  <td align="center" width="20px">:</td>
                                  <td valign="middle" class="py-1 font-size-18">
                                    <label class="m-0"><v-checkbox color="cyan" disabled :input-value="viewVisit?.visit?.is_service_form
                                      "></v-checkbox></label>
                                  </td>
                                </tr>
                                <tr>
                                  <td width="170px" valign="middle" class="py-1 font-size-18 font-weight-500">
                                    Line Item Visible In PDF
                                  </td>
                                  <td align="center" width="20px">:</td>
                                  <td valign="middle" class="py-1 font-size-18">
                                    <label class="m-0"><v-checkbox color="cyan" disabled :input-value="viewVisit?.visit?.is_item_pdf
                                      "></v-checkbox></label>
                                  </td>
                                </tr>
                                <tr>
                                  <td width="170px" valign="middle" class="py-1 font-size-18 font-weight-500">
                                    Payment collection needed
                                  </td>
                                  <td align="center" width="20px">:</td>
                                  <td valign="middle" class="py-1 font-size-18">
                                    <label class="m-0"><v-checkbox color="cyan" disabled :input-value="viewVisit?.ticket?.is_payment_needed
                                      "></v-checkbox></label>
                                  </td>
                                </tr>

                                <tr v-if="viewVisit?.visit?.quotation?.barcode">
                                  <td valign="middle" width="110px" class="py-1 font-size-18 font-weight-500">
                                    Quotation No
                                  </td>
                                  <td align="center" width="20px">:</td>
                                  <td valign="middle" class="py-1 font-size-18">
                                    <label class="m-0">
                                      <template v-if="
                                        viewVisit?.visit?.quotation?.barcode
                                      ">
                                        <Barcode dense small :barcode="viewVisit?.visit?.quotation?.barcode
                                          " :id="viewVisit?.visit?.quotation?.id" :route="'quotation.detail'">
                                        </Barcode>
                                      </template>
                                      <em v-else class="text-muted">
                                        no quotation number</em>
                                    </label>
                                  </td>
                                </tr>
                                <tr v-if="viewVisit?.visit?.enquiry?.barcode">
                                  <td valign="middle" width="110px" class="py-1 font-size-18 font-weight-500">
                                    Enquiry No
                                  </td>
                                  <td align="center" width="20px">:</td>
                                  <td valign="middle" class="py-1 font-size-18">
                                    <label class="m-0">
                                      <template v-if="
                                        viewVisit?.visit?.enquiry?.barcode
                                      ">
                                        <Barcode dense small :barcode="viewVisit?.visit?.enquiry?.barcode
                                          " :id="viewVisit?.visit?.enquiry?.id" :route="'enquiry.detail'"></Barcode>
                                      </template>
                                      <em v-else class="text-muted">
                                        no enquiry number</em>
                                    </label>
                                  </td>
                                </tr>
                                <tr>
                                  <td valign="middle" class="py-1 font-size-18 font-weight-500">
                                    PO Number
                                  </td>
                                  <td align="center">:</td>
                                  <td valign="middle" class="py-1 font-size-18">
                                    <label class="m-0">
                                      <template v-if="viewVisit?.ticket?.po_number">
                                        {{
                                          viewVisit?.ticket?.po_number
                                        }}</template>
                                      <em v-else class="text-muted">
                                        no po number</em>
                                    </label>
                                  </td>
                                </tr>
                                <tr>
                                  <td valign="middle" class="py-1 font-size-18 font-weight-500">
                                    Payment Mode
                                  </td>
                                  <td align="center">:</td>
                                  <td valign="middle" class="py-1 font-size-18">
                                    <template v-if="
                                      viewVisit?.ticket?.payment_mode?.text
                                    ">
                                      {{
                                        viewVisit?.ticket?.payment_mode?.text
                                      }}</template>
                                    <em v-else class="text-muted">
                                      no Payment Mode</em>
                                  </td>
                                </tr>
                                <tr>
                                  <td valign="top" class="py-1 font-size-18 font-weight-500">
                                    Job Type
                                  </td>
                                  <td align="center">:</td>
                                  <td valign="middle" class="py-1 font-size-18">
                                    <v-tooltip v-for="(type, index) in viewVisit?.ticket
                                      ?.ticket_type" :key="'ticket-type' + index" top
                                      content-class="custom-top-tooltip">
                                      <template v-slot:activator="{ on, attrs }">
                                        <v-chip v-bind="attrs" v-on="on" class="mr-2 font-weight-600"
                                          color="chip-custom-blue" label small outlined>
                                          {{ type }}
                                        </v-chip>
                                      </template>
                                      <span>Job Type</span>
                                    </v-tooltip>
                                  </td>
                                </tr>
                                <tr v-if="false">
                                  <td valign="top" class="py-1 font-size-18 font-weight-500">
                                    License
                                  </td>
                                  <td align="center">:</td>
                                  <td valign="middle" class="py-1 font-size-18">
                                    <template v-if="viewVisit?.ticket?.license">
                                      <v-tooltip v-for="(type, index) in viewVisit
                                        ?.ticket?.license" :key="'ticket-type' + index" top
                                        content-class="custom-top-tooltip">
                                        <template v-slot:activator="{ on, attrs }">
                                          <v-chip v-bind="attrs" v-on="on" class="mr-2 font-weight-600"
                                            color="chip-custom-blue" label small outlined>
                                            {{ type }}
                                          </v-chip>
                                        </template>
                                        <span>Job Type</span>
                                      </v-tooltip>
                                    </template>
                                    <template v-else>
                                      <em class="text-muted">no license</em>
                                    </template>
                                  </td>
                                </tr>
                              </table>
                            </v-flex>
                          </v-card-text>
                        </v-card>
                      </v-col>
                      <v-col md="6">
                        <v-card flat class="custom-grey-border remove-border-radius mb-4 h-100">
                          <v-card-title class="headline grey lighten-4 py-1">
                            <span class="font-weight-700 custom-headline color-custom-blue font-size-16 text-uppercase">
                              Billing Address</span>
                          </v-card-title>
                          <v-card-text class="p-6 font-size-16">
                            <v-flex md12>
                              <table width="100%">
                                <tr v-if="
                                  viewVisit?.ticket?.billing?.property_address
                                ">
                                  <td class="py-0 font-size-18 pt-2">
                                    <template>
                                      <div class="custom-flex-d" v-if="
                                        viewVisit?.ticket?.billing
                                          ?.property_name
                                      ">
                                        <b>
                                          <v-icon style="
                                              font-size: 18px;
                                              margin-top: -3px !important;
                                            " class="cmt-2">
                                            mdi-home-map-marker
                                          </v-icon>
                                        </b>
                                        <span>{{
                                          viewVisit?.ticket?.billing
                                            ?.property_name
                                        }}<br /></span>
                                      </div>
                                      <div v-else>
                                        <em class="text-muted">
                                          <v-icon style="
                                              font-size: 18px;
                                              margin-top: -3px !important;
                                            " class="cmt-2">
                                            mdi-home-map-marker
                                          </v-icon>
                                          no Site Name</em>
                                      </div>
                                    </template>
                                    <template>
                                      <div class="custom-flex-d">
                                        <b>
                                          <v-icon style="
                                              font-size: 18px;
                                              margin-top: -3px !important;
                                            " class="cmt-2">
                                            mdi-map-marker
                                          </v-icon>
                                        </b>
                                        <span>
                                          <template v-if="
                                            viewVisit?.ticket?.billing.unit_no
                                          ">{{
                                            viewVisit?.ticket?.billing
                                              .unit_no
                                          }},</template>
                                          <template v-if="
                                            viewVisit?.ticket?.billing
                                              .street_1
                                          ">{{
                                            viewVisit?.ticket?.billing
                                              .street_1
                                          }},</template>
                                          {{
                                            viewVisit?.ticket?.billing.street_2
                                          }}
                                          <br />
                                          {{
                                            viewVisit?.ticket?.billing.country
                                          }}
                                          <template v-if="
                                            viewVisit?.ticket?.billing
                                              .zip_code &&
                                            viewVisit?.ticket?.billing
                                              .zip_code != '000000'
                                          ">
                                            {{
                                              viewVisit?.ticket?.billing
                                                .zip_code
                                            }}
                                          </template>
                                        </span>
                                      </div>
                                    </template>
                                    <!--  <a
                                    target="_blank"
                                    :href="`https://maps.google.com/?q=${detail.billing.property_address}`"
                                    >{{ detail.billing.property_address }}</a
                                  > -->
                                  </td>
                                </tr>
                                <tr>
                                  <th class="">Contact details</th>
                                </tr>
                                <tr v-if="viewVisit?.ticket?.billing_person">
                                  <td class="py-0 font-size-18">
                                    <template v-if="
                                      viewVisit?.ticket?.billing_person
                                        .display_name
                                    ">
                                      {{
                                        viewVisit?.ticket?.billing_person
                                          .display_name
                                      }}
                                    </template>
                                  </td>
                                </tr>
                                <tr v-if="viewVisit?.ticket?.billing_person">
                                  <td class="py-0 font-size-18">
                                    <template v-if="
                                      viewVisit?.ticket?.billing_person
                                        .primary_phone
                                    ">
                                      {{
                                        viewVisit?.ticket?.billing_person
                                          .primary_phone
                                      }}
                                    </template>
                                  </td>
                                </tr>
                                <tr v-if="viewVisit?.ticket?.billing_person">
                                  <td class="py-0 font-size-18">
                                    <template v-if="
                                      viewVisit?.ticket?.billing_person
                                        .primary_email
                                    ">
                                      {{
                                        viewVisit?.ticket?.billing_person
                                          .primary_email
                                      }}
                                      <v-icon v-if="
                                        viewVisit?.ticket
                                          ?.billing_person_notify
                                      " v-tippy="{
                                        arrow: true,
                                        arrowType: 'round',
                                        animation: 'fade',
                                      }" content="Notification was sent to given email"
                                        color="green lighten-1">mdi-check</v-icon>
                                    </template>
                                  </td>
                                </tr>
                              </table>
                            </v-flex>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                    <v-card flat class="custom-grey-border remove-border-radius mb-4"
                      v-if="viewVisit?.visit?.status == 4">
                      <v-card-title class="headline grey lighten-4">
                        <span class="font-weight-700 custom-headline color-custom-blue font-size-16">Closer
                          Detail</span>
                      </v-card-title>
                      <!--  <v-card-text class="p-6 font-size-16"> -->
                      <TicketCloserDetail is-visit-detail :visit-detail="viewVisit?.visit" :detail="viewVisit?.ticket"
                        :customer="viewVisit?.ticket?.customer" :property="viewVisit?.ticket?.property"
                        :billing="viewVisit?.ticket?.billing" :contact-person="viewVisit?.ticket?.property_person"
                        :page-loading="viewLoading" />
                      <!--  </v-card-text> -->
                    </v-card>
                    <v-card v-if="viewVisit?.visit?.cancel_reason" flat
                      class="custom-grey-border remove-border-radius mb-4">
                      <v-card-title class="headline grey lighten-4">
                        <span class="font-weight-700 custom-headline color-custom-blue font-size-16">Cancelled
                          Reason</span>
                      </v-card-title>
                      <v-card-text class="p-6 font-size-16">
                        <read-more class="mx-4 custom-read-more font-weight-600 font-size-18 pb-1 custom-gray-color"
                          more-str="read more" :text="viewVisit?.visit?.cancel_reason ||
                            '<em>No Cancelled Reason</em>'
                            " link="#" less-str="read less" :max-chars="150">
                        </read-more>
                      </v-card-text>
                    </v-card>
                    <v-layout>
                      <v-flex md6>
                        <v-card flat class="custom-grey-border remove-border-radius mb-4">
                          <v-card-title class="headline grey lighten-4 py-1">
                            <span
                              class="font-weight-700 custom-headline color-custom-blue font-size-16 text-uppercase">Description</span>
                          </v-card-title>
                          <v-card-text class="p-6 font-size-16">
                            <read-more class="mx-4 custom-read-more font-weight-600 font-size-18 pb-1 custom-gray-color"
                              more-str="read more" :text="viewVisit?.ticket?.description ||
                                '<em>No Description</em>'
                                " link="#" less-str="read less" :max-chars="200">
                            </read-more>
                          </v-card-text>
                        </v-card>
                      </v-flex>
                      <v-flex md6 class="">
                        <v-card flat class="custom-grey-border remove-border-radius mb-4">
                          <v-card-title class="headline grey lighten-4 py-1">
                            <span class="font-weight-700 custom-headline color-custom-blue font-size-16">TERMS &amp;
                              CONDITIONS</span>
                          </v-card-title>
                          <v-card-text class="p-6 font-size-16">
                            <read-more class="mx-4 custom-read-more font-weight-600 font-size-18 pb-1 custom-gray-color"
                              more-str="read more" :text="viewVisit?.ticket?.term_conditions ||
                                '<em>No Terms &amp; Conditions</em>'
                                " link="#" less-str="read less" :max-chars="200">
                            </read-more>
                          </v-card-text>
                        </v-card>
                      </v-flex>
                    </v-layout>
                    <v-card flat class="custom-grey-border remove-border-radius mb-4">
                      <v-card-title class="headline grey lighten-4 py-1">
                        <span
                          class="font-weight-700 custom-headline color-custom-blue font-size-16 text-uppercase">Notes</span>
                      </v-card-title>
                      <v-card-text class="p-6 font-size-16">
                        <v-layout class="mb-4">
                          <v-flex md6 class="custom-border-right">
                            <v-card-title class="headline grey lighten-4 py-1">
                              <span class="font-weight-700 custom-headline color-custom-blue font-size-16">Client Note
                              </span>
                            </v-card-title>
                            <read-more class="mx-4 custom-read-more font-weight-600 font-size-18 pb-1 custom-gray-color"
                              more-str="read more" :text="viewVisit?.ticket?.client_remark ||
                                '<em>no Client Note</em>'
                                " link="#" less-str="read less" :max-chars="200">
                            </read-more>
                          </v-flex>
                          <v-flex md6>
                            <v-card-title class="headline grey lighten-4 py-1">
                              <span class="font-weight-700 custom-headline color-custom-blue font-size-16">Admin
                                Note</span>
                              <v-spacer></v-spacer>
                              <span class="d-flex" style="
                                  margin-top: -3px !important;
                                  font-size: 14px !important;
                                ">
                                <template v-if="viewVisit?.visit?.is_remark_show">
                                  <v-icon color="green" size="18">mdi-check-circle</v-icon>
                                  <span class="ml-1 green--text">Show In Application</span>
                                </template>
                              </span>
                            </v-card-title>

                            <read-more class="mx-4 custom-read-more font-weight-600 font-size-18 pb-1 custom-gray-color"
                              more-str="read more" :text="viewVisit?.ticket?.admin_remark ||
                                '<em>No Admin Note </em>'
                                " link="#" less-str="read less" :max-chars="200">
                            </read-more>
                          </v-flex>
                        </v-layout>
                      </v-card-text>
                    </v-card>
                  </v-container>
                </v-tab-item>
                <v-tab-item value="team">
                  <VisitTeamDetail :detail="{ id: viewVisit?.ticket?.id }" :visit-id="viewVisit?.visit?.id"
                    v-on:delete:engineer="getVisit(viewVisit?.visit)" />
                </v-tab-item>
              </v-tabs-items>
            </v-tabs>
          </v-col>
        </v-row>
      </template>
    </Dialog>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import { QUERY, GET, PUT } from "@/core/services/store/request.module";
import { GET_AVAILABLE_TEAM } from "@/core/services/store/visit.module";
import CommonMixin from "@/core/plugins/common-mixin";
import Dialog from "@/view/components/Dialog.vue";
import Barcode from "@/view/pages/partials/Barcode.vue";
import Priority from "@/view/pages/partials/Priority.vue";
import TicketCloserDetail from "@/view/pages/job/partials/Ticket-Closer-Detail.vue";
import VisitTeamDetail from "@/view/pages/job/partials/Visit-Team-Detail.vue";
import { ErrorEventBus, InitializeError } from "@/core/lib/message.lib";
import moment from "moment-timezone";

moment.tz.setDefault(process.env.VUE_APP_TIMEZONE);

const visitStatusList = [
  {
    text: "All Jobs",
    value: "all",
    count: "0",
    color: "cyan",
  },
  {
    text: "Unassigned Jobs",
    value: "unassigned",
    count: "0",
    color: "green",
  },
  {
    text: "Assigned Jobs",
    value: "assigned",
    count: "0",
    color: "red",
  },
  {
    text: "Open Jobs",
    value: "open",
    count: "0",
    color: "orange",
  },
  {
    text: "In-Progress Jobs",
    value: "in-progress",
    count: "0",
    color: "purple",
  },
  {
    text: "Checked-In Jobs",
    value: "checked-in",
    count: "0",
    color: "blue",
  },
];

export default {
  name: "live-tracking-visit-sidebar",
  mixins: [CommonMixin],
  props: {
    activeVisitUuid: {
      type: String,
      default: null,
    },
    date: {
      type: String,
      default: null,
    },
    engineerId: {
      type: Number,
      default: null,
    },
  },
  watch: {
    date(newValue) {
      if (newValue) {
        this.filterVisit();
      }
    },
    engineerId(newValue, oldValue) {
      if (newValue || oldValue) {
        this.filterVisit();
        this.getVisitStatus();
      }
    },
  },
  components: {
    Dialog,
    Barcode,
    Priority,
    TicketCloserDetail,
    VisitTeamDetail,
  },
  data() {
    return {
      ticketTab: "overview",
      search: null,
      timeout: null,
      timeoutDuration: 500,
      trackingVisitStatusList: visitStatusList,
      trackingVisitStatus: visitStatusList[1],
      trackingVisits: [],
      trackingVisitLoading: false,
      trackingVisit: {},
      viewVisit: {},
      viewDialog: false,
      viewLoading: false,
      engineerMenu: false,
      apptEngLoading: false,
      engineerList: [],
      newEngineers: [],
    };
  },
  methods: {
    getFollowColor(data) {
      if (data == 1) {
        return "green";
      } else {
        return "red";
      }
    },
    getStatusColor(status) {
      const STATUS_COLOR_MAP = {
        1: "blue darken-1",
        2: "yellow darken-1",
        3: "orange darken-4",
        4: "green darken-1",
        5: "red lighten-1",
        6: "orange darken-4",
      };
      return STATUS_COLOR_MAP[status] || null;
    },
    async getVisit(row) {
      try {
        const { data } = await this.$store.dispatch(GET, {
          url: `visit/v1/${row.id}`,
        });
        this.viewVisit = data;
        return true;
      } catch (err) {
        throw err;
      }
    },
    openDialog(row) {
      this.viewLoading = true;
      this.viewDialog = true;
      this.viewVisit = {};

      this.ticketTab = "overview";
      this.engineerMenu = false;
      this.apptEngLoading = false;
      this.engineerList = [];
      this.newEngineers = [];

      this.getVisit(row).finally(() => {
        this.viewLoading = false;
      });
    },
    filterVisit() {
      clearTimeout(this.timeout);
      this.trackingVisitLoading = true;
      this.trackingVisits = [];
      this.timeout = setTimeout(() => {
        // this.getVisitStatus();
        this.getVisits({
          date: this.date,
          search: this.search,
          engineer: this.engineerId,
        });
      }, this.timeoutDuration);
    },
    updateVisitStatus(row) {
      if (row?.engineer) {
        return false;
      }
      const engineerId = this.engineerId;

      this.$emit("clearEngineer", true);
      this.$nextTick(() => {
        this.trackingVisitStatus = row;
        this.filterVisit();
        if (engineerId) {
          this.getVisitStatus(row);
        }
      });
    },
    getAvailableEngineers() {
      let request = {
        start_date: moment(this.viewVisit?.visit?.started_at).format(
          "YYYY-MM-DD"
        ),
        start_time: moment(this.viewVisit?.visit?.started_at).format("HH:mm"),
        end_date: moment(this.viewVisit?.visit?.finished_at).format(
          "YYYY-MM-DD"
        ),
        end_time: moment(this.viewVisit?.visit?.finished_at).format("HH:mm"),
        force: 0,
        all: 1,
      };
      this.apptEngLoading = true;
      this.$store
        .dispatch(GET_AVAILABLE_TEAM, request)
        .then((output) => {
          if (output) {
            this.engineerList = this.vAvailableTeam;
            this.engineerMenu = true;
          }
        })
        .finally(() => {
          this.apptEngLoading = false;
        });
    },
    updateEngineer() {
      if (!this.newEngineers.length) {
        ErrorEventBus.$emit(
          "update:error",
          InitializeError("Please select an engineer then try again.")
        );
        return false;
      }
      this.apptEngLoading = true;
      this.$store
        .dispatch(PUT, {
          url:
            "job/" +
            this.viewVisit?.ticket?.id +
            "/visit/" +
            this.viewVisit?.visit?.id +
            "/engineer",
          data: {
            engineer: this.newEngineers,
          },
        })
        .then(() => {
          this.engineerList = [];
          this.$nextTick(() => {
            this.getVisit(this.viewVisit?.visit);
            this.filterVisit();
            this.getVisitStatus();
          });
        })
        .finally(() => {
          this.apptEngLoading = false;
        });
    },
    getVisits(query) {
      this.$store
        .dispatch(QUERY, {
          url: "tracking/map/visits",
          data: {
            ...query,
            status: this.trackingVisitStatus?.value ?? "all",
          },
        })
        .then((response) => {
          this.trackingVisits = response?.data?.visits ?? [];
          const routes = response?.data?.routes ?? [];
          this.$emit("update:visits", this.trackingVisits);
          this.$emit("update:visit-routes", routes);
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.trackingVisitLoading = false;
        });
    },
    getVisitStatus(status) {
      this.$store
        .dispatch(QUERY, {
          url: "tracking/visits/status",
          data: { date: this.date, engineer: this.engineerId },
        })
        .then((response) => {
          this.trackingVisitStatusList = response?.data ?? [];
          if (status?.value) {
            this.trackingVisitStatus = this.trackingVisitStatusList.find(
              (row) => row.value == status?.value
            );
          } else {
            this.trackingVisitStatus = this.trackingVisitStatusList.length
              ? this.trackingVisitStatusList[this.engineerId ? 0 : 1]
              : visitStatusList[1];
          }
        })
        .catch((error) => {
          this.logError(error);
        });
    },
    getDueDay(day) {
      if (day == 2) {
        return 15;
      } else if (day == 3) {
        return 30;
      } else if (day == 4) {
        return 45;
      }
    },
  },
  mounted() {
    this.filterVisit();
    this.getVisitStatus();
  },
  computed: {
    ...mapGetters(["vAvailableTeam"]),
    getAppointmentText() {
      if (this.viewVisit?.visit) {
        let date = this.formatDate(this.viewVisit?.visit?.started_at);
        let startTime = moment(this.viewVisit?.visit?.started_at).format(
          "hh:mm A"
        );
        let enddate = this.formatDate(this.viewVisit?.visit?.finished_at);
        let endTime = moment(this.viewVisit?.visit?.finished_at).format(
          "hh:mm A"
        );
        return `<div>From - ${date} ${startTime} </div>
                <div>To&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; - ${enddate} ${endTime} </div>`;
      }
      return null;
    },
    getScheduleType() {
      if (this.viewVisit?.ticket?.type === 1) {
        return "Adhoc job";
      }
      if (this.viewVisit?.ticket?.type === 2) {
        return "Recurring Job";
      }
      return null;
    },
    dialogWidth() {
      return this.lodash.toSafeInteger((document.body.clientWidth / 100) * 90);
    },
  },
};
</script>
